import { IconButton, Paper, styled, Tooltip } from "@mui/material";
import { getGridStringOperators, GridColDef } from "@mui/x-data-grid";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useNavigate } from "react-router-dom";
import { hasPermission } from "../../../components";
import Permissions from "../../../constants/permissions";
import useAuth from "../../../context/UserContext";
import { User } from "../../../graphql/types";

import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/MagnifyingGlass";
import { getFilterRolesOperator, getRenderCell } from "./FilterRolesComponent";

const Container = styled(Paper)({
  padding: "16px",
  height: "60vh",
  width: "100%",
});

const ViewDetailsIconStyled = styled(MagnifyingGlassIcon)(({ theme }) => ({
  color: theme.palette.text40.main,
}));

const nameEmailOperators = getGridStringOperators().filter(({ value }) =>
  ["equals", "contains", "startsWith"].includes(value)
);

const allUsersColumns: GridColDef[] = [
  {
    field: "name",
    headerName: "Name",
    editable: true,
    flex: 1,
    filterOperators: nameEmailOperators,
  },
  {
    field: "email",
    headerName: "Email",
    editable: true,
    flex: 1,
    filterOperators: nameEmailOperators,
  },
];

const globalRolesColumn: GridColDef = {
  field: "globalRoles",
  headerName: "Global Roles",
  width: 400,
  sortable: false,
  renderCell: (params) => {
    return getRenderCell({ rowField: params.row.globalRoles });
  },
  filterOperators: getFilterRolesOperator({ rowField: "globalRoles" }),
};

const UsersTable = ({ users, loading }: { users: User[]; loading: boolean }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const rows: {
    id: string;
    name: string;
    email: string;
    globalRoles: string[] | null;
  }[] = [];
  if (!loading) {
    users.forEach((user: User) => {
      rows.push({
        id: user.id,
        name: user.name,
        email: user.email,
        globalRoles: user.globalRoles || null,
      });
    });
  }
  const actionsColumn: GridColDef = {
    field: "actions",
    headerName: "Actions",
    width: 120,
    sortable: false,
    headerAlign: "center",
    align: "center",
    renderCell: (params) => {
      return (
        <>
          {params.row.email !== user?.email ? (
            <Tooltip title="View details" placement="bottom" arrow>
              <IconButton
                aria-label={`view details of ${params.row.email}`}
                onClick={() => {
                  if (params.row.email !== user?.email) {
                    navigate(`/users/${encodeURIComponent(params.row.id)}`);
                  }
                }}
              >
                <ViewDetailsIconStyled />
              </IconButton>
            </Tooltip>
          ) : null}
        </>
      );
    },
    filterable: false,
    hideSortIcons: true,
  };
  return (
    <Container variant="outlined" data-testid="users-table">
      <DataGridPro
        rows={rows}
        columns={
          hasPermission(Permissions.ManageGlobalUsers, user)
            ? [...allUsersColumns, globalRolesColumn, actionsColumn]
            : [...allUsersColumns, actionsColumn]
        }
        onRowClick={(params) => {
          if (params.row.email !== user?.email) {
            navigate(`/users/${encodeURIComponent(params.row.id)}`);
          }
        }}
        disableRowSelectionOnClick
        hideFooterPagination
        loading={loading}
        disableColumnPinning
        showToolbar
        disableDensitySelector
        isCellEditable={() => false}
      />
    </Container>
  );
};
export { UsersTable };
